<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4">
    <header class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold text-foreground">
        {{ title }}
      </h3>
      <div class="flex items-center gap-2">
        <span class="text-xs text-muted-foreground">{{ countLabel }}</span>
        <Button v-if="minimzed" type="button" size="icon-sm" variant="ghost" class="size-8" @click="toggleExpanded">
          <ChevronDown v-if="isMinimized" class="h-4 w-4" />
          <ChevronUp v-else class="h-4 w-4" />
          <span class="sr-only">{{ expandButtonLabel }}</span>
        </Button>
      </div>
    </header>

    <template v-if="!isMinimized">
      <p v-if="errorMessage" class="text-sm text-destructive">
        {{ errorMessage }}
      </p>

      <p v-if="eventLogsStore.isLoading && eventLogsStore.items.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        Loading event logs...
      </p>

      <div v-else-if="eventLogsStore.items.length > 0" class="flex flex-col gap-3">
        <EventLog
          v-for="eventLog in eventLogsStore.items"
          :key="eventLog.id"
          :event-log="eventLog"
        />

        <Pagination
          v-if="showPagination"
          v-model:page="eventLogsStore.page"
          :items-per-page="eventLogsStore.perPage"
          :total="eventLogsStore.total ?? 0"
        />
      </div>

      <p v-else class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        No event logs found for this record.
      </p>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ChevronDown, ChevronUp } from 'lucide-vue-next'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Pagination } from '../../../../../../../resources/js/components/ui/pagination/index.js'
import { useEventLogsStore } from '../../stores/eventLogsStore.js'
import EventLog from './EventLog.vue'

const props = defineProps({
  eventable_id: {
    type: [Number, String],
    required: true,
  },
  eventable_type: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Event Logs',
  },
  per_page: {
    type: Number,
    default: 10,
  },
  context: {
    type: String,
    default: null,
  },
  minimzed: {
    type: Boolean,
    default: false,
  },
})

const isExpanded = ref(false)
const resolvedContext = computed(() => {
  if (typeof props.context === 'string' && props.context.trim().length > 0) {
    return props.context.trim()
  }

  return `event_logs_card_${String(props.eventable_type).replace(/[^A-Za-z0-9]/g, '_')}_${props.eventable_id}`
})

const eventLogsStore = useEventLogsStore(resolvedContext.value)
const isMinimized = computed(() => props.minimzed && !isExpanded.value)
const expandButtonLabel = computed(() => (isMinimized.value ? 'Expand' : 'Collapse'))

const normalizedPerPage = computed(() => {
  const perPage = Number(props.per_page)

  if (!Number.isInteger(perPage) || perPage < 1) {
    return 10
  }

  return Math.min(perPage, 100)
})

const countLabel = computed(() => {
  const total = Number(eventLogsStore.total ?? eventLogsStore.items.length ?? 0)
  return `${total} total`
})

const showPagination = computed(() => {
  const total = Number(eventLogsStore.total ?? 0)

  if (total <= 0) {
    return false
  }

  return total > Number(eventLogsStore.perPage ?? normalizedPerPage.value)
})

const errorMessage = computed(() => {
  if (!eventLogsStore.error) {
    return null
  }

  return eventLogsStore.error.response?.data?.message ?? eventLogsStore.error.message ?? 'Unable to load event logs.'
})

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const loadEventLogs = async () => {
  eventLogsStore.perPage = normalizedPerPage.value

  await eventLogsStore.fetchItemsForEventable(props.eventable_id, props.eventable_type, {
    page: eventLogsStore.page,
    per_page: normalizedPerPage.value,
  })
}

const resetPaginationAndLoad = async () => {
  const shouldLoadImmediately = eventLogsStore.page === 1
  eventLogsStore.page = 1

  if (shouldLoadImmediately) {
    await loadEventLogs()
  }
}

watch(() => [props.eventable_id, props.eventable_type], async () => {
  isExpanded.value = false
  await resetPaginationAndLoad()
})

watch(() => props.per_page, async () => {
  await resetPaginationAndLoad()
})

watch(() => eventLogsStore.page, async (newPage, oldPage) => {
  if (newPage === oldPage) {
    return
  }

  await loadEventLogs()
})

onMounted(async () => {
  await loadEventLogs()
})
</script>
